/**
 * Biology Student Notes - Years 11-13
 * Based on Vanuatu National Syllabus
 * Comprehensive study materials for all strands
 */

// Year 11 Biology Student Notes
export const year11BiologyStudentNotes = {
  strand1: {
    title: "Cellular Biology",
    overview: "Understanding the physiological properties of cell structure, organelles, and their interactions with the environment",
    image: "https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=400",
    units: [
      {
        id: "unit1.1",
        title: "Cell Structure",
        content: `
## Cell Structure

### Key Definitions
- **Cell**: The basic structural and functional unit of all living organisms
- **Organelle**: A specialized structure within a cell that performs a specific function
- **Prokaryote**: A cell without a membrane-bound nucleus (e.g., bacteria)
- **Eukaryote**: A cell with a membrane-bound nucleus (plants, animals, fungi)

### Specialized Animal Cells
1. **Nerve cells (Neurons)**: Long extensions for signal transmission
2. **Muscle cells**: Elongated fibers for contraction
3. **Red blood cells**: Biconcave disc shape, no nucleus, carry oxygen
4. **Sperm cells**: Flagellum for movement, streamlined head
5. **Egg cells (Ova)**: Large, contain nutrients for early development

### Specialized Plant Cells
1. **Guard cells**: Bean-shaped, control stomata opening
2. **Phloem cells**: Sieve tube elements and companion cells
3. **Xylem cells**: Dead cells forming tubes for water transport

### Cell Organelles and Functions
| Organelle | Structure | Function |
|-----------|-----------|----------|
| Nucleus | Double membrane with pores | Contains DNA, controls cell activities |
| Mitochondria | Double membrane, inner folds (cristae) | ATP production (cellular respiration) |
| Ribosome | Small granule (RNA + protein) | Protein synthesis |
| Endoplasmic Reticulum | Network of membranes | Rough ER: protein transport; Smooth ER: lipid synthesis |
| Golgi Apparatus | Stacked flat membranes | Modifies, packages, and secretes proteins |
| Cell Membrane | Phospholipid bilayer | Controls entry/exit of substances |
| Cell Wall | Cellulose (plants) | Provides support and protection |
| Chloroplast | Double membrane, contains thylakoids | Photosynthesis |
| Vacuole | Membrane-bound sac | Storage, maintains turgor pressure |

### Calculating Cell Size
Formula: Actual Size = Measured Size ÷ Magnification
- Example: If a cell measures 5mm under 400x magnification
- Actual size = 5mm ÷ 400 = 0.0125mm = 12.5 μm
`,
        keyTerms: [
          { term: "Organelle", definition: "A specialized structure within a cell performing a specific function" },
          { term: "Cytoplasm", definition: "Jelly-like substance filling the cell, where organelles are suspended" },
          { term: "Cell membrane", definition: "Selectively permeable barrier controlling what enters and exits the cell" },
          { term: "Nucleus", definition: "Control center of the cell containing genetic material (DNA)" },
          { term: "Mitochondria", definition: "Powerhouse of the cell where ATP is produced through respiration" }
        ],
        practiceQuestions: [
          "Draw and label a typical animal cell with at least 6 organelles",
          "Compare the structure of plant and animal cells",
          "Explain why red blood cells lack a nucleus"
        ],
        examTips: ["Always label diagrams clearly", "Use specific terminology when describing functions"]
      },
      {
        id: "unit1.2",
        title: "Cell Transport",
        content: `
## Cell Transport

### Types of Transport

#### Passive Transport (No energy required)
1. **Diffusion**: Movement of particles from high to low concentration
2. **Facilitated Diffusion**: Diffusion assisted by carrier or channel proteins
3. **Osmosis**: Diffusion of water through a selectively permeable membrane

#### Active Transport (Energy required)
- Movement of substances against concentration gradient
- Requires ATP energy
- Uses carrier proteins

#### Cytosis (Bulk Transport)
1. **Endocytosis**: Taking materials into the cell
   - Phagocytosis: "Cell eating" - engulfing solids
   - Pinocytosis: "Cell drinking" - engulfing liquids
2. **Exocytosis**: Releasing materials from the cell

### Concentration Gradient
- **High concentration → Low concentration** = Down the gradient (passive)
- **Low concentration → High concentration** = Up the gradient (active)

### Surface Area to Volume Ratio
As cells grow larger:
- Volume increases faster than surface area
- Ratio decreases
- Less efficient exchange of materials
- This limits cell size

### Osmosis Effects on Cells

| Solution Type | Animal Cell | Plant Cell |
|---------------|-------------|------------|
| Hypotonic (dilute) | Swells, may burst (lysis) | Becomes turgid |
| Isotonic (equal) | Normal | Normal |
| Hypertonic (concentrated) | Shrinks (crenation) | Plasmolysis |

### Key Terms
- **Hypotonic**: Solution with lower solute concentration than inside cell
- **Isotonic**: Solution with equal solute concentration to inside cell
- **Hypertonic**: Solution with higher solute concentration than inside cell
`,
        keyTerms: [
          { term: "Diffusion", definition: "Movement of particles from high to low concentration" },
          { term: "Osmosis", definition: "Diffusion of water across a selectively permeable membrane" },
          { term: "Active transport", definition: "Movement of substances against concentration gradient using energy" },
          { term: "Turgid", definition: "Plant cell swollen with water, firm due to turgor pressure" },
          { term: "Plasmolysis", definition: "Shrinking of plant cell contents away from cell wall due to water loss" }
        ],
        practiceQuestions: [
          "Explain the difference between passive and active transport",
          "Describe what happens to a red blood cell placed in distilled water",
          "Calculate the surface area to volume ratio for a cube with sides of 3cm"
        ],
        examTips: ["Draw diagrams showing particle movement", "Use arrows to show direction of transport"]
      },
      {
        id: "unit1.3",
        title: "Enzymes",
        content: `
## Enzymes

### Key Definitions
- **Enzyme**: A biological catalyst that speeds up chemical reactions
- **Catalyst**: A substance that increases reaction rate without being used up
- **Substrate**: The molecule on which an enzyme acts
- **Active site**: The region of an enzyme where the substrate binds

### Enzyme Properties
1. Made of protein
2. Specific to substrate (lock and key model / induced fit model)
3. Reusable
4. Work best at optimal conditions
5. Can be denatured by extreme conditions

### Induced Fit Model
- Enzyme shape changes slightly when substrate binds
- Provides better fit between enzyme and substrate
- More accurate than simple lock and key model

### Enzyme Specificity
1. **Substrate specificity**: Each enzyme only works on specific substrate(s)
2. **Specificity of action**: Each enzyme catalyzes only one type of reaction

### Factors Affecting Enzyme Activity

#### Temperature
- Increases activity up to optimum (usually 37°C for human enzymes)
- Beyond optimum: enzyme denatures (shape changes)
- Too cold: reaction slows but enzyme not damaged

#### pH
- Each enzyme has optimal pH
- Pepsin: pH 2 (stomach)
- Amylase: pH 7 (mouth)
- Extreme pH causes denaturation

#### Substrate Concentration
- More substrate = faster reaction (up to a point)
- Maximum rate when all active sites occupied
- Graph shows saturation curve

#### Enzyme Concentration
- More enzyme = faster reaction (if enough substrate)
- Linear relationship

#### Inhibitors
- **Competitive inhibitors**: Block active site
- **Non-competitive inhibitors**: Change enzyme shape

#### Co-factors and Coenzymes
- Some enzymes need additional molecules to function
- Co-factors: usually metal ions (Mg²⁺, Zn²⁺)
- Coenzymes: usually organic molecules (vitamins)
`,
        keyTerms: [
          { term: "Enzyme", definition: "Biological catalyst made of protein that speeds up reactions" },
          { term: "Substrate", definition: "Molecule that binds to enzyme's active site" },
          { term: "Denaturation", definition: "Irreversible change in enzyme shape due to extreme conditions" },
          { term: "Active site", definition: "Region of enzyme where substrate binds" },
          { term: "Induced fit", definition: "Model where enzyme shape slightly changes to better fit substrate" }
        ],
        practiceQuestions: [
          "Explain the induced fit model of enzyme action",
          "Describe how temperature affects enzyme activity",
          "What is the difference between competitive and non-competitive inhibitors?"
        ],
        examTips: ["Draw and interpret enzyme activity graphs", "Explain shape change causes loss of function"]
      }
    ]
  },
  strand2: {
    title: "Genetics",
    overview: "Understanding molecular structure and function of genes, heredity and variation in living organisms",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400",
    units: [
      {
        id: "unit2.1",
        title: "Molecular Genetics",
        content: `
## Molecular Genetics

### The Nucleus and Its Components
- **Nucleus**: Contains genetic material (DNA)
- **Chromosome**: DNA molecule wrapped around proteins (histones)
- **Gene**: Section of DNA that codes for a specific protein
- **Allele**: Different versions of the same gene

### DNA Structure
- Double helix (twisted ladder)
- Made of nucleotides
- Each nucleotide has:
  - Sugar (deoxyribose)
  - Phosphate group
  - Nitrogenous base (A, T, G, or C)

### Base Pairing Rules
- Adenine (A) pairs with Thymine (T)
- Guanine (G) pairs with Cytosine (C)
- Bases held together by hydrogen bonds

### RNA Structure
- Single stranded
- Sugar is ribose (not deoxyribose)
- Uracil (U) replaces Thymine (T)

### Protein Synthesis

#### 1. Transcription (in nucleus)
- DNA unzips
- mRNA copy made from one DNA strand
- mRNA leaves nucleus through nuclear pore

#### 2. Translation (in cytoplasm)
- mRNA attaches to ribosome
- tRNA brings amino acids
- Codon (mRNA) matches anticodon (tRNA)
- Amino acids joined by peptide bonds
- Protein chain formed

### Key Differences
| Feature | Transcription | Translation |
|---------|---------------|-------------|
| Location | Nucleus | Cytoplasm (ribosome) |
| Template | DNA | mRNA |
| Product | mRNA | Protein |
| Direction | 5' to 3' | 5' to 3' |
`,
        keyTerms: [
          { term: "Gene", definition: "Section of DNA coding for a specific protein" },
          { term: "Allele", definition: "Alternative form of a gene" },
          { term: "Transcription", definition: "Process of copying DNA to mRNA in the nucleus" },
          { term: "Translation", definition: "Process of assembling proteins from mRNA at ribosomes" },
          { term: "Codon", definition: "Three-base sequence on mRNA coding for one amino acid" }
        ],
        practiceQuestions: [
          "Describe the structure of a DNA nucleotide",
          "Explain the base pairing rules in DNA and RNA",
          "Compare and contrast transcription and translation"
        ],
        examTips: ["Know the differences between DNA and RNA", "Practice writing complementary base sequences"]
      },
      {
        id: "unit2.2",
        title: "Cell Growth and Division",
        content: `
## Cell Growth and Division

### DNA Replication
- **Semi-conservative**: Each new DNA has one old and one new strand
- Occurs before cell division
- Ensures genetic information passed to daughter cells

### Replication Steps
1. Helicase unwinds and unzips DNA
2. DNA polymerase adds complementary nucleotides
3. Ligase joins fragments together

### Mitosis
Cell division for growth, repair, and asexual reproduction

#### Phases of Mitosis
1. **Prophase**: Chromosomes condense, nuclear membrane breaks down
2. **Metaphase**: Chromosomes line up at cell equator
3. **Anaphase**: Sister chromatids separate, move to opposite poles
4. **Telophase**: Nuclear membranes reform, chromosomes decondense
5. **Cytokinesis**: Cytoplasm divides, two cells formed

### Meiosis
Cell division for producing gametes (sex cells)

#### Key Features
- Two divisions: Meiosis I and Meiosis II
- Results in 4 haploid cells (half the chromosome number)
- Creates genetic variation through:
  - Crossing over (exchange of genetic material)
  - Independent assortment (random chromosome arrangement)

### Comparison of Mitosis and Meiosis
| Feature | Mitosis | Meiosis |
|---------|---------|---------|
| Number of divisions | 1 | 2 |
| Daughter cells | 2 | 4 |
| Chromosome number | Same as parent (diploid) | Half of parent (haploid) |
| Genetic variation | No (clones) | Yes |
| Purpose | Growth, repair | Gamete production |
`,
        keyTerms: [
          { term: "Mitosis", definition: "Cell division producing two identical diploid daughter cells" },
          { term: "Meiosis", definition: "Cell division producing four haploid gametes" },
          { term: "Diploid", definition: "Having two complete sets of chromosomes (2n)" },
          { term: "Haploid", definition: "Having one complete set of chromosomes (n)" },
          { term: "Crossing over", definition: "Exchange of genetic material between homologous chromosomes" }
        ],
        practiceQuestions: [
          "List the stages of mitosis in order",
          "Explain how meiosis creates genetic variation",
          "Why is DNA replication described as semi-conservative?"
        ],
        examTips: ["Draw labeled diagrams of each mitosis stage", "Know the chromosome numbers before and after each division"]
      }
    ]
  },
  strand3: {
    title: "Organism Level Biology",
    overview: "Understanding organization among plants and animals, structure and function in vital processes",
    image: "https://images.unsplash.com/photo-1518837695005-2083093ee35b?w=400",
    units: [
      {
        id: "unit3.1",
        title: "Reproduction in Plants",
        content: `
## Reproduction in Plants

### Types of Reproduction

#### Asexual Reproduction
- One parent only
- Offspring genetically identical (clones)
- Examples: runners (strawberry), bulbs, tubers

**Advantages:**
- Fast
- No need for pollinator
- Successful characteristics preserved

**Disadvantages:**
- No genetic variation
- Vulnerable to disease

#### Sexual Reproduction
- Two parents (male and female gametes)
- Offspring genetically varied
- Involves meiosis and fertilization

### Life Cycles - Alternation of Generations

#### Sporophyte Generation
- Diploid (2n)
- Produces spores by meiosis
- Dominant in ferns and flowering plants

#### Gametophyte Generation
- Haploid (n)
- Produces gametes by mitosis
- Dominant in mosses

### Plant Groups and Reproduction

#### Mosses (Bryophytes)
- Gametophyte dominant
- Need water for fertilization
- Spores dispersed by wind

#### Ferns (Pteridophytes)
- Sporophyte dominant
- Need water for fertilization
- Spores on underside of fronds (sori)

#### Gymnosperms (Conifers)
- Sporophyte dominant
- Seeds exposed on cones
- Wind pollination

#### Angiosperms (Flowering plants)
- Sporophyte dominant
- Seeds enclosed in fruit
- Various pollination methods (insects, wind, water)
`,
        keyTerms: [
          { term: "Alternation of generations", definition: "Life cycle alternating between diploid sporophyte and haploid gametophyte" },
          { term: "Sporophyte", definition: "Diploid generation that produces spores" },
          { term: "Gametophyte", definition: "Haploid generation that produces gametes" },
          { term: "Pollination", definition: "Transfer of pollen from anther to stigma" },
          { term: "Fertilization", definition: "Fusion of male and female gametes" }
        ],
        practiceQuestions: [
          "Compare sexual and asexual reproduction in plants",
          "Explain the alternation of generations in ferns",
          "Why do mosses need water for reproduction?"
        ],
        examTips: ["Draw life cycle diagrams", "Know which generation is dominant in each plant group"]
      },
      {
        id: "unit3.2",
        title: "Human Nutrition and Animal Digestion",
        content: `
## Human Nutrition and Animal Digestion

### Pacific Diet Comparison

#### Traditional Diet
- Root crops (taro, yam, kumara)
- Fish and seafood
- Coconut
- Fresh fruits and vegetables

**Advantages:**
- Low in processed sugar
- High in fiber
- Natural nutrients

#### Modern Diet
- Processed foods
- High sugar and salt
- Refined carbohydrates
- Fast food

**Health Impacts:**
- Diabetes
- Obesity
- Heart disease
- Non-Communicable Diseases (NCDs)

### Digestive System Processes

1. **Ingestion**: Taking food into the mouth
2. **Digestion**: Breaking down food (mechanical + chemical)
3. **Absorption**: Nutrients enter bloodstream
4. **Egestion**: Removal of undigested waste

### Digestive Organs and Functions
| Organ | Function |
|-------|----------|
| Mouth | Mechanical breakdown, amylase breaks starch |
| Stomach | Protein digestion (pepsin), HCl kills bacteria |
| Small intestine | Main digestion and absorption site |
| Large intestine | Water absorption, forms feces |
| Liver | Produces bile for fat emulsification |
| Pancreas | Produces digestive enzymes |

### Digestive Enzymes
| Enzyme | Substrate | Product | Location |
|--------|-----------|---------|----------|
| Amylase | Starch | Maltose | Mouth, small intestine |
| Pepsin | Protein | Peptides | Stomach |
| Lipase | Fats | Fatty acids + glycerol | Small intestine |
| Trypsin | Protein | Amino acids | Small intestine |
`,
        keyTerms: [
          { term: "Digestion", definition: "Breakdown of large food molecules into smaller, absorbable ones" },
          { term: "Absorption", definition: "Movement of nutrients from gut into bloodstream" },
          { term: "NCDs", definition: "Non-Communicable Diseases such as diabetes and heart disease" },
          { term: "Bile", definition: "Liver secretion that emulsifies fats in the small intestine" },
          { term: "Villi", definition: "Finger-like projections in small intestine increasing surface area" }
        ],
        practiceQuestions: [
          "Compare traditional and modern Pacific diets",
          "Explain the role of bile in fat digestion",
          "List the enzymes involved in protein digestion and where they act"
        ],
        examTips: ["Know enzyme names, substrates, products, and locations", "Relate diet to health issues in Vanuatu"]
      }
    ]
  },
  strand4: {
    title: "Environmental Biology",
    overview: "Understanding how ecosystems are affected by human activities and organism adaptations",
    image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400",
    units: [
      {
        id: "unit4.1",
        title: "Ecosystems and Biodiversity",
        content: `
## Ecosystems and Biodiversity

### Key Definitions
- **Population**: Group of organisms of the same species in an area
- **Community**: All different species living together in an area
- **Ecosystem**: Community plus its physical environment
- **Biodiversity**: Variety of life in an ecosystem

### Ecosystem Components

#### Biotic Factors (Living)
- Producers (plants)
- Consumers (animals)
- Decomposers (bacteria, fungi)

#### Abiotic Factors (Non-living)
- Temperature
- Light
- Water
- Soil
- Nutrients

### Food Chains and Webs
- **Producer** → Primary Consumer → Secondary Consumer → Tertiary Consumer
- Energy decreases at each trophic level (only ~10% transferred)
- Food webs show interconnected food chains

### Energy Flow
- Sun → Producers (photosynthesis) → Consumers → Decomposers
- Energy lost as heat at each level
- Pyramids of energy always upright

### Human Impacts on Ecosystems
1. **Deforestation**: Habitat loss, soil erosion
2. **Pollution**: Air, water, land contamination
3. **Overfishing**: Disrupts marine food webs
4. **Introduced species**: Compete with native species

### Biodiversity and Survival
- High biodiversity = more stable ecosystem
- Loss of one species affects food web
- Genetic diversity improves adaptation
`,
        keyTerms: [
          { term: "Ecosystem", definition: "Community of organisms interacting with their physical environment" },
          { term: "Biodiversity", definition: "Variety of species in an ecosystem" },
          { term: "Food chain", definition: "Linear sequence showing energy transfer between organisms" },
          { term: "Trophic level", definition: "Position in a food chain (producer, primary consumer, etc.)" },
          { term: "Decomposer", definition: "Organism that breaks down dead organic matter" }
        ],
        practiceQuestions: [
          "Draw a food web for a local Vanuatu ecosystem",
          "Explain why energy decreases at each trophic level",
          "Discuss how deforestation affects biodiversity"
        ],
        examTips: ["Use local examples from Vanuatu", "Know the difference between food chain and food web"]
      },
      {
        id: "unit4.2",
        title: "Classification and Adaptations",
        content: `
## Classification and Adaptations

### Classification System
- **Kingdom** → Phylum → Class → Order → Family → Genus → Species
- Remember: "King Philip Came Over For Good Soup"

### The Five Kingdoms
1. **Monera**: Bacteria (prokaryotes)
2. **Protista**: Single-celled eukaryotes
3. **Fungi**: Decomposers, absorb nutrients
4. **Plantae**: Photosynthetic organisms
5. **Animalia**: Multicellular consumers

### Scientific Naming
- Binomial nomenclature (two-part name)
- Genus + species (italicized)
- Example: *Homo sapiens* (humans)

### Adaptations

#### Structural Adaptations
- Physical features for survival
- Examples: bird beaks, cactus spines

#### Physiological Adaptations
- Internal body processes
- Examples: venom production, hibernation

#### Behavioral Adaptations
- Actions that improve survival
- Examples: migration, nocturnal activity

### Ecological Concepts

#### Ecological Niche
- Role of an organism in its ecosystem
- Includes habitat, food, and interactions

#### Gause's Principle
- Two species cannot occupy the same niche
- Competition leads to exclusion

#### Liebig's Law
- Growth limited by scarcest resource
- The "limiting factor" principle
`,
        keyTerms: [
          { term: "Classification", definition: "Organizing organisms into groups based on similarities" },
          { term: "Binomial nomenclature", definition: "Two-part naming system using genus and species" },
          { term: "Adaptation", definition: "Characteristic helping an organism survive in its environment" },
          { term: "Ecological niche", definition: "Role of an organism within its ecosystem" },
          { term: "Limiting factor", definition: "Resource in shortest supply that limits growth" }
        ],
        practiceQuestions: [
          "List the seven levels of classification in order",
          "Give examples of structural, physiological, and behavioral adaptations",
          "Explain Gause's competitive exclusion principle"
        ],
        examTips: ["Use correct scientific names with proper format", "Link adaptations to specific environments"]
      }
    ]
  },
  strand5: {
    title: "Variation and Evolution",
    overview: "Understanding variation in organisms and hereditary characteristics leading to evolution",
    image: "https://images.unsplash.com/photo-1504006833117-8886a355efbf?w=400",
    units: [
      {
        id: "unit5.1",
        title: "Natural Selection",
        content: `
## Natural Selection

### Darwin's Theory
Charles Darwin developed the theory of natural selection based on observations during his voyage on HMS Beagle.

### Key Points of Natural Selection
1. **Overproduction**: Organisms produce more offspring than can survive
2. **Variation**: Individuals differ in characteristics
3. **Competition**: Struggle for resources
4. **Survival of the fittest**: Best adapted individuals survive
5. **Reproduction**: Survivors pass on favorable traits

### Evidence for Evolution
1. **Fossil record**: Shows change over time
2. **Comparative anatomy**: Similar structures (homologous)
3. **Embryology**: Similar early development
4. **Molecular biology**: DNA similarities
5. **Biogeography**: Distribution of species

### Natural vs Artificial Selection

| Feature | Natural Selection | Artificial Selection |
|---------|------------------|---------------------|
| Selective agent | Environment | Humans |
| Speed | Slow | Fast |
| Purpose | Survival | Human benefit |
| Examples | Darwin's finches | Dog breeds, crops |

### Adaptive Radiation
- Single ancestor gives rise to many species
- Each adapted to different niche
- Example: Darwin's finches on Galápagos Islands
- Different beak shapes for different foods
`,
        keyTerms: [
          { term: "Natural selection", definition: "Process where organisms with favorable traits survive and reproduce" },
          { term: "Artificial selection", definition: "Human selection of organisms with desired traits for breeding" },
          { term: "Adaptive radiation", definition: "Evolution of many species from a common ancestor" },
          { term: "Fitness", definition: "Ability of an organism to survive and reproduce" },
          { term: "Homologous structures", definition: "Similar structures in different species due to common ancestry" }
        ],
        practiceQuestions: [
          "Describe the five main points of Darwin's theory",
          "Compare natural selection and artificial selection",
          "Explain adaptive radiation using Darwin's finches as an example"
        ],
        examTips: ["Know multiple lines of evidence for evolution", "Be able to explain natural selection step by step"]
      }
    ]
  }
};

// Year 12 Biology Student Notes
export const year12BiologyStudentNotes = {
  strand1: {
    title: "Cellular Biology",
    overview: "Understanding processes, mechanisms and significance of respiration, photosynthesis and cellular immunity",
    image: "https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=400",
    units: [
      {
        id: "unit1.1",
        title: "Respiration",
        content: `
## Cellular Respiration

### Overview
- Process of breaking down glucose to release energy (ATP)
- Occurs in the mitochondria
- Equation: C₆H₁₂O₆ + 6O₂ → 6CO₂ + 6H₂O + ATP

### Three Stages of Aerobic Respiration

#### 1. Glycolysis (Cytoplasm)
- Glucose (6C) → 2 Pyruvate (3C)
- Net gain: 2 ATP
- No oxygen required

#### 2. Krebs Cycle (Mitochondrial Matrix)
- Pyruvate converted to Acetyl CoA
- Produces: CO₂, NADH, FADH₂, 2 ATP

#### 3. Electron Transport Chain (Inner Mitochondrial Membrane)
- NADH and FADH₂ donate electrons
- Oxygen is final electron acceptor
- Produces most ATP (34 ATP)

### Aerobic vs Anaerobic Respiration

| Feature | Aerobic | Anaerobic |
|---------|---------|-----------|
| Oxygen | Required | Not required |
| ATP yield | 36-38 ATP | 2 ATP |
| Products | CO₂ + H₂O | Lactic acid OR Ethanol + CO₂ |
| Location | Cytoplasm + Mitochondria | Cytoplasm only |
`,
        keyTerms: [
          { term: "ATP", definition: "Adenosine triphosphate - energy currency of cells" },
          { term: "Glycolysis", definition: "Breakdown of glucose into pyruvate in cytoplasm" },
          { term: "Krebs cycle", definition: "Cycle of reactions in mitochondrial matrix producing electron carriers" },
          { term: "Electron transport chain", definition: "Series of proteins generating most ATP in respiration" },
          { term: "Anaerobic respiration", definition: "Respiration without oxygen, producing less ATP" }
        ],
        practiceQuestions: [
          "Compare the ATP yield of aerobic and anaerobic respiration",
          "Describe what happens during glycolysis",
          "Explain the role of oxygen in the electron transport chain"
        ],
        examTips: ["Know where each stage occurs", "Practice drawing mitochondria structure"]
      },
      {
        id: "unit1.2",
        title: "Photosynthesis",
        content: `
## Photosynthesis

### Overview
- Process of converting light energy to chemical energy
- Occurs in chloroplasts
- Equation: 6CO₂ + 6H₂O + Light → C₆H₁₂O₆ + 6O₂

### Chloroplast Structure
- **Thylakoids**: Membrane-bound sacs where light reactions occur
- **Grana**: Stacks of thylakoids
- **Stroma**: Fluid-filled space where Calvin cycle occurs

### Two Stages of Photosynthesis

#### 1. Light-Dependent Reactions (Thylakoid membrane)
- Light absorbed by chlorophyll
- Water split (photolysis): H₂O → O₂ + H⁺ + e⁻
- ATP and NADPH produced

#### 2. Light-Independent Reactions / Calvin Cycle (Stroma)
- CO₂ fixed by RuBisCO enzyme
- ATP and NADPH used
- Glucose produced

### Limiting Factors
- **Light intensity**: More light = faster rate (up to saturation)
- **CO₂ concentration**: More CO₂ = faster rate (up to saturation)
- **Temperature**: Optimal around 25-35°C
`,
        keyTerms: [
          { term: "Chlorophyll", definition: "Green pigment that absorbs light for photosynthesis" },
          { term: "Thylakoid", definition: "Membrane structure where light reactions occur" },
          { term: "Calvin cycle", definition: "Series of reactions fixing CO₂ into glucose" },
          { term: "Limiting factor", definition: "Factor in shortest supply that limits reaction rate" },
          { term: "Photolysis", definition: "Splitting of water molecules using light energy" }
        ],
        practiceQuestions: [
          "Draw and label a chloroplast",
          "Explain how limiting factors affect photosynthesis rate",
          "Compare the products of light-dependent and light-independent reactions"
        ],
        examTips: ["Relate photosynthesis to respiration", "Draw graphs showing limiting factor effects"]
      }
    ]
  },
  strand2: {
    title: "Genetics",
    overview: "Understanding Mendelian inheritance, diversity and relatedness",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400",
    units: [
      {
        id: "unit2.1",
        title: "Mendelian Inheritance",
        content: `
## Mendelian Inheritance

### Mendel's Experiments
- Used pea plants
- Studied one trait at a time (monohybrid cross)
- Studied two traits (dihybrid cross)

### Key Terms
- **Dominant allele**: Expressed when one or two copies present (A)
- **Recessive allele**: Only expressed when two copies present (a)
- **Homozygous**: Same alleles (AA or aa)
- **Heterozygous**: Different alleles (Aa)
- **Genotype**: Genetic makeup (AA, Aa, aa)
- **Phenotype**: Physical expression (tall, short)

### Mendel's Laws
1. **Law of Segregation**: Allele pairs separate during meiosis
2. **Law of Independent Assortment**: Different traits inherited independently

### Types of Dominance

#### Complete Dominance
- Heterozygote same as dominant homozygote
- Example: Aa looks like AA

#### Incomplete Dominance
- Heterozygote intermediate between parents
- Example: Red × White = Pink flower

#### Codominance
- Both alleles fully expressed
- Example: AB blood type

### Punnett Square
Tool for predicting offspring genotypes and phenotypes
- Monohybrid cross: 3:1 phenotype ratio
- Dihybrid cross: 9:3:3:1 phenotype ratio
`,
        keyTerms: [
          { term: "Allele", definition: "Alternative form of a gene" },
          { term: "Genotype", definition: "Genetic makeup of an organism" },
          { term: "Phenotype", definition: "Physical expression of genotype" },
          { term: "Punnett square", definition: "Grid used to predict offspring genotypes" },
          { term: "Incomplete dominance", definition: "Heterozygote shows intermediate phenotype" }
        ],
        practiceQuestions: [
          "Complete a monohybrid cross for heterozygous tall pea plants",
          "Explain the difference between incomplete dominance and codominance",
          "What is the expected ratio for a dihybrid cross?"
        ],
        examTips: ["Practice Punnett squares thoroughly", "Always define your allele symbols"]
      }
    ]
  },
  strand3: {
    title: "Organism Level Biology",
    overview: "Understanding plant structure, circulatory systems, excretion and nervous system",
    image: "https://images.unsplash.com/photo-1518837695005-2083093ee35b?w=400",
    units: [
      {
        id: "unit3.1",
        title: "Circulatory Systems",
        content: `
## Animal Circulatory Systems

### Why Large Animals Need Internal Transport
- Surface area to volume ratio decreases with size
- Diffusion too slow for large organisms
- Need efficient delivery of O₂, nutrients, removal of waste

### Types of Circulatory Systems

#### Open Circulatory System
- Blood (hemolymph) not always in vessels
- Low pressure
- Found in: insects, crustaceans
- Less efficient

#### Closed Circulatory System
- Blood always in vessels
- Higher pressure
- More efficient

### Heart Chambers

| Animal | Chambers | Circulation Type |
|--------|----------|------------------|
| Fish | 2 (1 atrium, 1 ventricle) | Single circulation |
| Amphibians | 3 (2 atria, 1 ventricle) | Double, incomplete |
| Reptiles | 3 or 4 | Double, mostly incomplete |
| Mammals/Birds | 4 (2 atria, 2 ventricles) | Double, complete |

### Single vs Double Circulation
- **Single**: Blood passes through heart once per circuit
- **Double**: Blood passes through heart twice (to lungs + to body)

### Double circulation advantages:
- Higher pressure to body
- More efficient oxygen delivery
- Supports higher metabolic rate
`,
        keyTerms: [
          { term: "Single circulation", definition: "Blood passes through heart once per circuit (fish)" },
          { term: "Double circulation", definition: "Blood passes through heart twice - pulmonary and systemic" },
          { term: "Open circulation", definition: "Blood not always contained in vessels" },
          { term: "Closed circulation", definition: "Blood always in vessels" },
          { term: "Atrium", definition: "Heart chamber receiving blood" }
        ],
        practiceQuestions: [
          "Compare single and double circulation",
          "Explain why mammals need a four-chambered heart",
          "What are the advantages of closed over open circulation?"
        ],
        examTips: ["Draw diagrams of different heart structures", "Link heart structure to animal activity level"]
      }
    ]
  },
  strand4: {
    title: "Environmental Biology",
    overview: "Understanding communities, populations and their characteristics",
    image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400",
    units: [
      {
        id: "unit4.1",
        title: "Populations",
        content: `
## Population Ecology

### Population Characteristics
- **Size**: Number of individuals
- **Density**: Individuals per unit area
- **Distribution**: How individuals are spread (clumped, random, uniform)

### Population Growth

#### J-Shaped Curve (Exponential)
- Unlimited resources
- No limiting factors
- Rapid increase

#### S-Shaped Curve (Logistic)
- Resources become limited
- Growth slows
- Reaches carrying capacity

### Carrying Capacity (K)
- Maximum population size environment can sustain
- Limited by resources (food, space, water)

### Factors Affecting Population Size

#### Density-Dependent Factors
- Effect increases with population density
- Examples: disease, competition, predation

#### Density-Independent Factors
- Effect same regardless of population size
- Examples: natural disasters, climate

### Survivorship Curves
- **Type I**: High survival until old age (humans)
- **Type II**: Constant mortality rate (birds, reptiles)
- **Type III**: High early mortality (fish, insects)
`,
        keyTerms: [
          { term: "Carrying capacity", definition: "Maximum population size an environment can sustain" },
          { term: "Natality", definition: "Birth rate of a population" },
          { term: "Mortality", definition: "Death rate of a population" },
          { term: "Density-dependent", definition: "Factor whose effect varies with population density" },
          { term: "Survivorship curve", definition: "Graph showing survival rate at different ages" }
        ],
        practiceQuestions: [
          "Describe the difference between J-shaped and S-shaped growth curves",
          "Give examples of density-dependent and density-independent factors",
          "What determines carrying capacity?"
        ],
        examTips: ["Practice drawing and interpreting growth curves", "Use examples from Vanuatu ecosystems"]
      }
    ]
  },
  strand5: {
    title: "Variation and Evolution",
    overview: "Understanding patterns of evolution and speciation",
    image: "https://images.unsplash.com/photo-1504006833117-8886a355efbf?w=400",
    units: [
      {
        id: "unit5.1",
        title: "Patterns of Evolution and Speciation",
        content: `
## Patterns of Evolution

### Divergent Evolution
- Common ancestor gives rise to different species
- Species become more different over time
- Results in homologous structures
- Example: mammal forelimbs (whale flipper, bat wing, human arm)

### Convergent Evolution
- Unrelated species develop similar features
- Similar environments lead to similar adaptations
- Results in analogous structures
- Example: wings of birds and insects

### Parallel Evolution
- Related species independently evolve similar traits
- Starting from similar ancestral condition
- Example: flightless birds on different islands

## Speciation

### Allopatric Speciation
- Geographic isolation leads to new species
- Physical barrier separates populations
- Example: mountains, rivers, islands

### Sympatric Speciation
- New species arise in same area
- No physical separation
- Due to behavioral or ecological differences

### Reproductive Isolating Mechanisms

#### Pre-zygotic (Before fertilization)
- Habitat isolation
- Temporal isolation (different mating times)
- Behavioral isolation
- Mechanical isolation
- Gametic isolation

#### Post-zygotic (After fertilization)
- Hybrid inviability (offspring die)
- Hybrid sterility (offspring cannot reproduce)
- Hybrid breakdown (F2 generation weak)
`,
        keyTerms: [
          { term: "Divergent evolution", definition: "Species become more different from common ancestor" },
          { term: "Convergent evolution", definition: "Unrelated species develop similar adaptations" },
          { term: "Allopatric speciation", definition: "New species formed due to geographic isolation" },
          { term: "Sympatric speciation", definition: "New species formed without geographic isolation" },
          { term: "Reproductive isolation", definition: "Mechanisms preventing interbreeding between species" }
        ],
        practiceQuestions: [
          "Compare divergent and convergent evolution with examples",
          "Explain how allopatric speciation occurs",
          "List three pre-zygotic isolating mechanisms"
        ],
        examTips: ["Use specific examples for each type of evolution", "Understand both types of speciation"]
      }
    ]
  }
};

// Year 13 Biology Student Notes
export const year13BiologyStudentNotes = {
  strand1: {
    title: "Cellular Biology",
    overview: "Understanding DNA replication and protein synthesis mechanisms",
    image: "https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=400",
    units: [
      {
        id: "unit1.1",
        title: "DNA Replication and Protein Synthesis",
        content: `
## DNA Replication

### Semi-conservative Replication
- Each new DNA molecule has one original and one new strand
- Proven by Meselson and Stahl experiment

### Steps of DNA Replication
1. **Helicase** unwinds and separates DNA strands
2. **Primase** adds RNA primer
3. **DNA Polymerase** adds nucleotides (5' to 3' direction)
4. **Leading strand**: Continuous synthesis
5. **Lagging strand**: Okazaki fragments formed
6. **Ligase** joins Okazaki fragments

### Potential Replication Errors
- Mutations (base substitutions)
- Proofreading by DNA polymerase
- Repair enzymes correct most errors

## Protein Synthesis

### Protein Structure Levels
1. **Primary**: Amino acid sequence
2. **Secondary**: Alpha helix, beta sheet
3. **Tertiary**: 3D folding
4. **Quaternary**: Multiple polypeptides

### The Genetic Code
- **Triplet code**: 3 bases = 1 amino acid
- **Universal**: Same code in all organisms
- **Degenerate**: Multiple codons for same amino acid
- **Non-overlapping**: Each base read only once

### Key Molecules in Protein Synthesis
| Molecule | Function |
|----------|----------|
| mRNA | Carries code from DNA to ribosome |
| tRNA | Brings amino acids, has anticodon |
| rRNA | Forms ribosome structure |
`,
        keyTerms: [
          { term: "Semi-conservative replication", definition: "Each new DNA has one old and one new strand" },
          { term: "Helicase", definition: "Enzyme that unwinds DNA double helix" },
          { term: "DNA polymerase", definition: "Enzyme that adds nucleotides during replication" },
          { term: "Okazaki fragments", definition: "Short DNA segments formed on lagging strand" },
          { term: "Codon", definition: "Three-base sequence coding for an amino acid" }
        ],
        practiceQuestions: [
          "Explain why DNA replication is called semi-conservative",
          "Describe the role of each enzyme in DNA replication",
          "What are the features of the genetic code?"
        ],
        examTips: ["Know the direction of DNA synthesis (5' to 3')", "Practice translating mRNA sequences"]
      }
    ]
  },
  strand2: {
    title: "Genetics",
    overview: "Understanding gene concepts, metabolic pathways, linkages and biotechnology",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400",
    units: [
      {
        id: "unit2.1",
        title: "Advanced Genetics and Biotechnology",
        content: `
## Gene Interactions

### Multiple Alleles
- More than two alleles for a gene in population
- Example: ABO blood group (I^A, I^B, i)

### Epistasis
- One gene affects expression of another
- Modified phenotype ratios
- Example: coat color in mice

### Polygenic Inheritance
- Multiple genes affect one trait
- Continuous variation
- Example: height, skin color

### Sex Linkage
- Genes on sex chromosomes
- X-linked more common than Y-linked
- Examples: color blindness, hemophilia

## Biotechnology

### DNA Profiling
- Uses Short Tandem Repeats (STRs)
- Each person's pattern unique
- Applications: forensics, paternity testing

### PCR (Polymerase Chain Reaction)
- Amplifies small amounts of DNA
- Steps: Denature → Anneal → Extend
- Repeat cycles double DNA each time

### Gel Electrophoresis
- Separates DNA fragments by size
- Smaller fragments travel further
- Visualized with UV light

### Gene Cloning
- Using bacterial plasmids
- Restriction enzymes cut DNA
- Ligase joins DNA fragments
- Recombinant DNA transformed into bacteria
`,
        keyTerms: [
          { term: "Epistasis", definition: "One gene masking the expression of another gene" },
          { term: "Polygenic inheritance", definition: "Multiple genes affecting a single trait" },
          { term: "Sex linkage", definition: "Genes located on sex chromosomes" },
          { term: "PCR", definition: "Technique for amplifying DNA samples" },
          { term: "Gel electrophoresis", definition: "Method for separating DNA fragments by size" }
        ],
        practiceQuestions: [
          "Explain why X-linked recessive traits are more common in males",
          "Describe the steps of PCR",
          "How is DNA profiling used in forensic science?"
        ],
        examTips: ["Practice calculating ratios for epistasis", "Know applications of biotechnology"]
      }
    ]
  },
  strand3: {
    title: "Organism Level Biology",
    overview: "Understanding ecological niches, animal behaviour and timing responses",
    image: "https://images.unsplash.com/photo-1518837695005-2083093ee35b?w=400",
    units: [
      {
        id: "unit3.1",
        title: "Animal Behaviour",
        content: `
## Animal Behaviour

### Types of Behaviour

#### Innate Behaviour
- Inherited, genetically determined
- Same in all members of species
- Examples: suckling, web spinning

#### Learned Behaviour
- Acquired through experience
- Can be modified
- Examples: hunting techniques, bird songs

### Orientation and Navigation

#### Taxes
- Directional movement in response to stimulus
- Positive (towards) or negative (away)
- Example: Phototaxis - movement toward light

#### Kinesis
- Non-directional change in activity
- Speed or turning frequency changes
- Example: Woodlice more active in dry conditions

### Navigation Methods
- **Sun compass**: Using sun position
- **Star patterns**: Nocturnal navigation
- **Magnetic fields**: Earth's magnetic field
- **Chemical trails**: Pheromones

## Biological Rhythms

### Types of Rhythms
- **Circadian**: ~24 hours (sleep/wake)
- **Circatidal**: ~12.4 hours (tidal)
- **Circalunar**: ~29.5 days (lunar)
- **Circannual**: ~1 year (seasonal)

### Key Terms
- **Endogenous rhythm**: Internal biological clock
- **Exogenous rhythm**: Response to external cues
- **Zeitgeber**: Environmental time cue (light, temperature)
- **Free-running period**: Rhythm without external cues
`,
        keyTerms: [
          { term: "Innate behaviour", definition: "Inherited behaviour present from birth" },
          { term: "Learned behaviour", definition: "Behaviour acquired through experience" },
          { term: "Taxis", definition: "Directional movement in response to stimulus" },
          { term: "Circadian rhythm", definition: "Approximately 24-hour biological cycle" },
          { term: "Zeitgeber", definition: "Environmental cue that entrains biological rhythms" }
        ],
        practiceQuestions: [
          "Compare innate and learned behaviour with examples",
          "Explain how birds navigate during migration",
          "What is the difference between taxes and kinesis?"
        ],
        examTips: ["Use specific animal examples", "Understand different navigation mechanisms"]
      }
    ]
  },
  strand4: {
    title: "Environmental Biology",
    overview: "Understanding climate change and conservation",
    image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400",
    units: [
      {
        id: "unit4.1",
        title: "Climate Change and Conservation",
        content: `
## Climate Change

### Greenhouse Effect
- Natural process keeping Earth warm
- Greenhouse gases trap heat
- Enhanced by human activities

### Greenhouse Gases
- Carbon dioxide (CO₂): Fossil fuels, deforestation
- Methane (CH₄): Livestock, rice paddies
- Nitrous oxide (N₂O): Fertilizers
- Water vapor (H₂O)

### Human Contributions
- Burning fossil fuels
- Deforestation
- Agriculture
- Industrial processes

### Climate Change Effects
1. Rising sea levels
2. Extreme weather events
3. Coral bleaching
4. Species migration/extinction
5. Changes in rainfall patterns

### Impacts on Pacific Islands
- Coastal erosion
- Saltwater intrusion
- Food security threats
- Loss of traditional livelihoods
- Climate refugees

## Conservation

### Adaptation Strategies
- Building sea walls
- Changing crop varieties
- Water conservation
- Relocating communities

### Mitigation Strategies
- Reduce emissions
- Renewable energy (solar, wind, hydro)
- Reforestation
- Energy efficiency
- Carbon capture

### Renewable Energy Sources
| Source | Advantages | Disadvantages |
|--------|------------|---------------|
| Solar | Clean, abundant | Intermittent, storage needed |
| Wind | Clean, renewable | Visual impact, variable |
| Hydro | Reliable, storage | Ecosystem disruption |
| Geothermal | Constant, efficient | Location limited |
`,
        keyTerms: [
          { term: "Greenhouse effect", definition: "Trapping of heat by atmospheric gases" },
          { term: "Climate change", definition: "Long-term changes in global climate patterns" },
          { term: "Adaptation", definition: "Adjusting to effects of climate change" },
          { term: "Mitigation", definition: "Reducing causes of climate change" },
          { term: "Renewable energy", definition: "Energy from sources that are naturally replenished" }
        ],
        practiceQuestions: [
          "Explain the enhanced greenhouse effect",
          "List five impacts of climate change on Pacific Islands",
          "Compare adaptation and mitigation strategies"
        ],
        examTips: ["Use local examples from Vanuatu and the Pacific", "Discuss both causes and solutions"]
      }
    ]
  },
  strand5: {
    title: "Variation and Evolution",
    overview: "Understanding variation, natural selection, gene pools and speciation",
    image: "https://images.unsplash.com/photo-1504006833117-8886a355efbf?w=400",
    units: [
      {
        id: "unit5.1",
        title: "Variation and Gene Pools",
        content: `
## Sources of Variation

### Meiosis Creates Variation Through:
1. **Crossing over**: Exchange of genetic material
2. **Independent assortment**: Random chromosome arrangement
3. **Random fertilization**: Any sperm can fertilize any egg

### Mutation
- Change in DNA sequence
- Source of new alleles
- Most neutral or harmful, few beneficial

## Gene Pool and Allele Frequency

### Gene Pool
- All alleles in a population
- Changes over time = evolution

### Factors Affecting Allele Frequency

#### Natural Selection
- Favors advantageous alleles
- Removes harmful alleles

#### Genetic Drift
- Random changes in allele frequency
- More significant in small populations

#### Founder Effect
- Small group colonizes new area
- Limited genetic diversity
- Different allele frequencies from original

#### Bottleneck Effect
- Population drastically reduced
- Loss of genetic diversity
- Example: cheetahs

#### Gene Flow
- Movement of alleles between populations
- Migration increases diversity

## Speciation Review

### Reproductive Isolating Mechanisms

Pre-mating:
- Habitat isolation
- Temporal isolation
- Behavioral isolation

Post-mating:
- Gametic isolation
- Hybrid inviability
- Hybrid sterility
- Hybrid breakdown

### Patterns of Evolution
- **Divergent**: From common ancestor, becoming different
- **Convergent**: Unrelated species becoming similar
- **Co-evolution**: Two species evolving together
`,
        keyTerms: [
          { term: "Gene pool", definition: "All the alleles in a population" },
          { term: "Genetic drift", definition: "Random change in allele frequency" },
          { term: "Founder effect", definition: "Loss of genetic variation when new population established" },
          { term: "Bottleneck effect", definition: "Reduction in genetic diversity after population crash" },
          { term: "Gene flow", definition: "Movement of alleles between populations" }
        ],
        practiceQuestions: [
          "Explain how meiosis creates genetic variation",
          "Compare the founder effect and bottleneck effect",
          "How does genetic drift differ from natural selection?"
        ],
        examTips: ["Know specific examples of genetic drift", "Understand how all factors affect evolution"]
      }
    ]
  }
};

export default {
  year11BiologyStudentNotes,
  year12BiologyStudentNotes,
  year13BiologyStudentNotes
};
